import {
  FORM_INVALID,
  PASSWORD_INVALID,
  EMAIL_INVALID } from '@/messages/errors'

/**
 * バリデーション関数
 */
const validateFunc = {
  // 必須チェック
  required: {
    handler(val, param, msg) {
      const fail = () => [false, msg || '$name$を入力してください。']

      if (val === '' || val === null) {
        return fail()
      }

      if (param) {
        if (param.deny.includes(val)) {
          return fail()
        }
      }

      return [true]
    }
  },
  // 同一性チェック
  same: {
    handler(val, param, msg) {
      if (val !== param) {
        return [false, msg]
      }
      return [true]
    },
    getParam: (val, vm) => vm.$data[val]
  },
  // メールアドレス形式チェック
  email: {
    handler(val) {
      const pattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

      if (!pattern.test(val)) {
        return [false, EMAIL_INVALID]
      }
  
      return [true]
    }
  },
  // パスワード形式チェック
  password: {
    // 8文字以上、半角小文字数字を含む
    handler(val) {
      if (val.length < 8) {
        return [false, PASSWORD_INVALID]
      }
  
      if (!/[a-z]+/.test(val)) {
        return [false, PASSWORD_INVALID]
      }
  
      if (!/[0-9]+/.test(val)) {
        return [false, PASSWORD_INVALID]
      }
  
      return [true]
    }
  },
  // 正規表現パターンチェック
  pattern: {
    handler(val, re, msg) {
      // 実質的に必須チェックとなってしまうことを避けるため、
      // 空であったときはパターンチェックを行わない
      if (val === '') {
        return [true]
      }

      if (!re.test(val)) {
        return [false, msg]
      }

      return [true]
    },
    getParam: val => new RegExp(val)
  },
  // 桁数チェック
  digits: {
    handler(val, max, msg) {
      if ((val + "").length > max) {
        return [false, msg || `$name$は${max}桁以内で入力してください。`]
      }
  
      return [true]
    }
  },
  // お子様情報チェック
  child: {
    handler(val, param, msg) {
      for (var i = 0; i < param.length; i++) {
        if (!(param[i].sex.length === 0 && param[i].year.length === 0 && param[i].month.length === 0) &&
            !(param[i].sex > 0 && param[i].year > 0 && param[i].month > 0)) {
            return [false, msg]
        }
      }
      return [true]
    },
    getParam: (val, vm) => { return vm.$data.childInfo }
  }
}

/**
 * 入力値バリデーション用ミックスイン
 */
export default {
  data() {
    return {
      errors: {}, // エラー
      invalidMessage: FORM_INVALID // 共通エラーメッセージ
    }
  },
  computed: {
    /**
     * どこかのキーでエラーが発生しているか
     * @returns {boolean} エラーがあるか？
     */
    hasErrors() {
      const keys = Object.keys(this.errors)

      if (keys.length === 0) {
        return false
      }

      for (const key of keys) {
        if (this.errors[key].length > 0) {
          return true
        }
      }

      return false
    }
  },
  methods: {
    /**
     * バリデーション実行
     * targetsを省略した場合はrulesに定義されたすべてのキーを検証する
     * @param {array} targets 検証したいキー
     * @returns {boolean} 検証結果が問題なかったか？
     */
    validate(targets) {
      const { rules } = this.$options

      if (!rules) {
        return true
      }

      const validateRules = typeof rules === 'function' ? rules(this) : rules

      this.errors = {}

      const validateTargets = targets
        ? Object.keys(validateRules).filter(name => targets.includes(name))
        : Object.keys(validateRules)

      validateTargets.forEach(name => {
        const label = validateRules[name][0]
        const ruleFuncs = validateRules[name][1]

        for (const rule of ruleFuncs) {
          const funcName = Array.isArray(rule) ? rule[0] : rule
          const option = Array.isArray(rule) ? rule[1] : null
          const message = Array.isArray(rule) ? rule[2] : ''
          const func = typeof funcName === 'object'
            ? funcName
            : validateFunc[funcName]

          if (typeof func === 'undefined') {
            return false
          }

          const { handler, getParam } = func

          // バリデーション関数に渡すパラメータを取得する
          // 関数によってオプションの解釈が異なる
          const param = getParam ? getParam(option, this) : option

          // 入力値を取得する
          const val = (() => {
            const keys = name.split('.')
            if (keys.length === 1) {
              return this.$data[keys[0]]
            }
            if (keys.length === 2) {
              return this.$data[keys[0]][keys[1]]
            }
          })()

          // バリデーション実行
          const [valid, msg] = handler(val, param, message)

          // エラーがなければ、後続処理に進まず、次のキーの検証に移る
          if (valid) {
            continue
          }

          // エラーメッセージをセットする
          if (!this.hasError(name)) {
            this.$set(this.errors, name, [])
          }
          const error = msg.replace('$name$', label)
          this.errors = Object.assign({}, this.errors, {
            [name]: [...this.errors[name], error]
          })

          // 必須チェックでエラーの場合は、後続のチェックを行わない
          // 未入力のフォームにこれ以上チェックを行っても仕方ないため
          if (funcName === 'required') {
            break
          }
        }
      })

      // 結果
      return Object.keys(this.$data.errors).length === 0
    },
    /**
     * 特定のキーのエラーメッセージを追加する
     * @param {string} key キー
     * @param {string} msg 追加したいエラーメッセージ
     */
    addValidateError(key, msg) {
      if (!this.hasError(key)) {
        this.$set(this.errors, key, [])
      }

      const label = this.$options.rules[key][0]
      const message = msg.replace('$name$', label)

      this.errors = Object.assign({}, this.errors, {
        [key]: [...this.errors[key], message]
      })
    },
    /**
     * 特定のキーのエラーメッセージを取り消す
     * @param {string} key キー
     * @param {string} msg 削除したいエラーメッセージ
     */
    removeValidateError(key, msg) {
      if (!this.hasError(key)) {
        return false
      }

      this.errors = Object.assign({}, this.errors, {
        [key]: this.errors[key].filter(m => m !== msg)
      })
    },
    /**
     * 特定のキーにエラーがあるか調べる
     * @param {*} key 調べたいキー
     * @returns {boolean} エラーがあるか？
     */
    hasError(key) {
      if (!this.errors[key]) {
        return false
      }

      return this.errors[key].length > 0
    },
    getErrors(key) {
      return this.errors[key] || []
    },
    clearErrors() {
      this.errors = {}
    },
  }
}
